import { useMemo } from "react";
import { Check, Loader2, ShieldQuestion, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes } from "@/lib/formatPort";
import { cn } from "@/lib/utils";
import type { PendingElicitation } from "./kaiTypes";
import {
  compactRateCardDiffHeader,
  formatRateCardDiffRoute,
  RateCardHeaderValue,
} from "./KaiRateCardDiffCard";
import { renderElicitationPreview } from "./kaiElicitationRegistry";

interface ParsedRateLine {
  idx: string;
  route: string;
  equip: string;
  frt: string;
  surcharges: string;
  allIn: string;
}

/**
 * Parse a rate-card preview elicitation message into structured header fields
 * and rate-line rows. Falls back gracefully so non-rate-card elicitations
 * still render their header key/value pairs.
 */
export function parseRateCardPreview(message: string) {
  const lines = message.split("\n").filter((l) => l.trim());
  const header: { label: string; value: string }[] = [];
  const rateLines: ParsedRateLine[] = [];
  let inRates = false;

  for (const line of lines) {
    if (line.startsWith("Rate Lines") || line.startsWith("───")) {
      inRates = line.startsWith("Rate Lines");
      continue;
    }
    if (line.startsWith("Rate Card Preview") || line.startsWith("─────")) continue;

    if (inRates) {
      const match = line.match(/^\s*(\d+)\.\s+(.+)$/);
      if (match) {
        const parts = match[2].split("|").map((s) => s.trim());
        const frtRaw = parts[2] ?? "";
        const allInRaw = parts[4] ?? "";
        rateLines.push({
          idx: match[1],
          route: parts[0] ?? "",
          equip: parts[1] ?? "",
          frt: frtRaw.replace(/^FRT\s*/i, ""),
          surcharges: parts[3] ?? "—",
          allIn: allInRaw.replace(/^All-in\s*/i, ""),
        });
      }
    } else {
      const colonIdx = line.indexOf(":");
      if (colonIdx > 0) {
        header.push({
          label: line.slice(0, colonIdx).trim(),
          value: line.slice(colonIdx + 1).trim(),
        });
      }
    }
  }

  return { header, rateLines };
}

interface KaiElicitationCardProps {
  elicitation: PendingElicitation;
  onRespond: (id: string, action: "accept" | "decline") => void;
}

/**
 * Inline confirmation card for an MCP elicitation (e.g. rate-card creation or
 * restriction change). The agent's tool call is blocked server-side until the
 * user approves or rejects, so this must render wherever Kai streams.
 */
export function KaiElicitationCard({
  elicitation,
  onRespond,
}: KaiElicitationCardProps) {
  const uiComponent = (elicitation.schema as { ui_component?: string })
    ?.ui_component;
  const structuredPreview = renderElicitationPreview(
    uiComponent,
    (elicitation.schema as { preview?: unknown })?.preview,
  );
  const { header: parsedHeader, rateLines } = parseRateCardPreview(
    elicitation.message,
  );
  const header = compactRateCardDiffHeader(parsedHeader);
  const { preferences } = useUserPreferences();
  const routeLocodes = useMemo(
    () => extractLaneLocodes(...rateLines.map((line) => line.route)),
    [rateLines],
  );
  const portNames = usePortNames(routeLocodes);

  const isPending = elicitation.status === "pending";
  const isSubmitting = elicitation.status === "submitting";
  const isError = elicitation.status === "error";

  const trimmedMessage = elicitation.message.trimStart();
  const isRestriction =
    uiComponent === "restriction_proposal" ||
    trimmedMessage.startsWith("Restriction Change") ||
    trimmedMessage.startsWith("Restriction Proposal") ||
    trimmedMessage.startsWith("Restriction Deletion");
  const isCustomerRequest =
    !isRestriction && trimmedMessage.startsWith("Customer Request");

  const noun = isRestriction
    ? "restriction change"
    : isCustomerRequest
      ? "support case"
      : "rate card";
  const cardTitle = isRestriction
    ? "Restriction Change Confirmation"
    : isCustomerRequest
      ? "Support Case Confirmation"
      : "Rate Card Confirmation";
  const cardDescription = isRestriction
    ? "Review the rule change. Confirming applies it immediately."
    : isCustomerRequest
      ? "Review the request and confirm before it is sent to Twido support."
      : "Review the parsed rate card and confirm creation.";

  const approveLabel = isRestriction
    ? "Apply change"
    : isCustomerRequest
      ? "Submit"
      : "Approve";
  const rejectLabel = isCustomerRequest || isRestriction ? "Cancel" : "Reject";

  if (!isPending && !isSubmitting) {
    const resolvedMessage = isError
      ? elicitation.errorMessage
        ? `Could not open ${noun}: ${elicitation.errorMessage}`
        : `Could not open ${noun}.`
      : elicitation.result === "approved"
        ? isRestriction
          ? "Restriction change applied"
          : isCustomerRequest
            ? "Support case opened and Twido notified"
            : "Rate card approved and created"
        : isRestriction
          ? "Restriction change cancelled"
          : isCustomerRequest
            ? "Support case cancelled"
            : "Rate card creation rejected";

    const tone = isError
      ? "destructive"
      : elicitation.result === "approved"
        ? "success"
        : "muted";

    return (
      <div
        className={cn(
          "w-full max-w-[min(100%,38rem)] rounded-xl border px-3 py-2.5",
          tone === "success" &&
            "border-emerald-500/20 bg-emerald-500/[0.06]",
          tone === "destructive" &&
            "border-destructive/25 bg-destructive/[0.06]",
          tone === "muted" && "border-border/40 bg-foreground/[0.03]",
        )}
        aria-label={`${cardTitle} ${elicitation.result ?? "closed"}`}
      >
        <div className="flex items-start gap-2.5">
          {tone === "success" ? (
            <Check
              className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
          ) : tone === "destructive" ? (
            <X
              className="mt-0.5 size-4 shrink-0 text-destructive"
              aria-hidden
            />
          ) : (
            <X
              className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-snug text-foreground">
              {cardTitle}
            </p>
            <p
              className={cn(
                "mt-0.5 text-xs",
                tone === "success" &&
                  "text-emerald-700 dark:text-emerald-300",
                tone === "destructive" && "text-destructive",
                tone === "muted" && "text-muted-foreground",
              )}
            >
              {resolvedMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel-strong clay-strong w-full max-w-[min(100%,38rem)] rounded-2xl px-4 py-3.5">
      <div className="flex items-center gap-2">
        <ShieldQuestion className="size-4 shrink-0 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">{cardTitle}</h3>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{cardDescription}</p>

      <div className="mt-3">
        {structuredPreview ? (
          structuredPreview
        ) : isRestriction ? (
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-foreground/90">
            {elicitation.message}
          </pre>
        ) : (
          <div className="flex flex-col gap-3">
            {header.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {header.map((h, i) => (
                  <div key={i} className="flex flex-col gap-0.5">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {h.label}
                    </span>
                    <RateCardHeaderValue label={h.label} value={h.value} />
                  </div>
                ))}
              </div>
            )}
            {rateLines.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-border/50">
                <table className="w-full border-collapse text-left text-[12px]">
                  <thead>
                    <tr className="border-b border-border/50 bg-foreground/[0.04] text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">#</th>
                      <th className="px-2 py-1.5 font-medium">Lane</th>
                      <th className="px-2 py-1.5 font-medium">Equipment</th>
                      <th className="px-2 py-1.5 font-medium">FRT</th>
                      <th className="px-2 py-1.5 font-medium">Surcharges</th>
                      <th className="px-2 py-1.5 font-medium">All-in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateLines.map((line, i) => (
                      <tr key={i} className="border-b border-border/30 last:border-b-0">
                        <td className="px-2 py-1.5 tabular-nums">{line.idx}</td>
                        <td className="px-2 py-1.5">
                          {formatRateCardDiffRoute(
                            line.route,
                            preferences.showLocodes,
                            portNames,
                          )}
                        </td>
                        <td className="px-2 py-1.5">{line.equip}</td>
                        <td className="px-2 py-1.5 tabular-nums">{line.frt}</td>
                        <td className="px-2 py-1.5">{line.surcharges}</td>
                        <td className="px-2 py-1.5 tabular-nums">{line.allIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {header.length === 0 && rateLines.length === 0 && (
              <p className="text-sm text-foreground/90">{elicitation.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="h-8 gap-1.5 rounded-xl px-3 text-xs"
          disabled={isSubmitting}
          onClick={() => onRespond(elicitation.elicitationId, "accept")}
        >
          {isSubmitting && <Loader2 className="size-3.5 animate-spin" />}
          {approveLabel}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded-xl px-3 text-xs"
          disabled={isSubmitting}
          onClick={() => onRespond(elicitation.elicitationId, "decline")}
        >
          {rejectLabel}
        </Button>
      </div>
    </div>
  );
}
